import { Drawer, Loading } from '@/components';
import {
  useCreateIndicator,
  useIndicator,
  useUpdateIndicator,
} from '@/features/project/_hooks/activity';
import { useMessage } from '@/providers';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';
import { IndicatorForm } from './IndicatorForm';

type Props = {
  projectId: string;
  fiscalYear: number;
};

export const IndicatorFormDrawer: FC<Props> = ({ projectId }) => {
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const { drawerState, setDrawerState } = useProjectDetailDrawerState();
  const { mutateAsync: createIndicator } = useCreateIndicator();
  const { mutateAsync: updateIndicator } = useUpdateIndicator();

  const isOpen =
    drawerState.open && drawerState.type === 'activity-indicator-form';
  const isEdit = isOpen && drawerState.isEditing;
  const indicatorId = isEdit ? drawerState.indicatorId : '';

  const indicator = useIndicator({
    id: indicatorId,
    enabled: isEdit,
  });

  const handleClose = async () => {
    setDrawerState({ open: false });
  };

  return (
    <Drawer open={isOpen} onClose={handleClose} destroyOnClose>
      {isEdit && indicator.isLoading ? (
        <Loading />
      ) : (
        <IndicatorForm
          type={isEdit ? 'edit' : 'new'}
          initialValues={{
            ...(indicator.data
              ? {
                  ...indicator.data,
                  noTypeReason: indicator.data.noTypeReason ?? undefined,
                  targetValue: indicator.data.targetValue ?? undefined,
                  term: 'term' in indicator.data ? indicator.data.term : null,
                  priorId: indicator.data.priorId ?? null,
                  dataNatureType:
                    'dataNatureType' in indicator.data
                      ? indicator.data.dataNatureType
                      : null,
                  description: indicator.data.description ?? '',
                }
              : undefined),
          }}
          onSubmit={async (values) => {
            isEdit
              ? await updateIndicator({
                  id: indicatorId,
                  ...values,
                })
              : await createIndicator({
                  json: { id: projectId },
                  ...values,
                });

            await Promise.all([
              openMessage(
                'success',
                isEdit
                  ? t('message-update-completed')
                  : t('message-create-completed'),
              ),
              handleClose(),
            ]);
          }}
        />
      )}
    </Drawer>
  );
};
